import { useEffect } from "react";
import { useSelector, useDispatch } from "react-redux";
import {
  selectDepartments,
  selectDesignations,
  selectDesignationsByDepartment,
  selectRoles,
  selectBranches,
  selectCategories,
  selectVendors,
  selectStaff,
  selectCustomers,
  selectLeadSources,
  selectLeadStatuses,
  setDepartmentsLoading,
  setDepartmentsSuccess,
  setDepartmentsError,
  setDesignationsLoading,
  setDesignationsSuccess,
  setDesignationsError,
  setDesignationsByDepartmentLoading,
  setDesignationsByDepartmentSuccess,
  setDesignationsByDepartmentError,
  setRolesLoading,
  setRolesSuccess,
  setRolesError,
  setBranchesLoading,
  setBranchesSuccess,
  setBranchesError,
  setCategoriesLoading,
  setCategoriesSuccess,
  setCategoriesError,
  setVendorsLoading,
  setVendorsSuccess,
  setVendorsError,
  setStaffLoading,
  setStaffSuccess,
  setStaffError,
  setCustomersLoading,
  setCustomersSuccess,
  setCustomersError,
  setLeadSourcesLoading,
  setLeadSourcesSuccess,
  setLeadSourcesError,
  setLeadStatusesLoading,
  setLeadStatusesSuccess,
  setLeadStatusesError,
  refreshOption,
} from "../store/slices/filterOptions.slice";
import { convertToLabelValue } from "../utils/common";
import { getDepartmentList } from "../services/workspace/department.service";
import { getDesignationList } from "../services/workspace/designation.service";
import { getRoleList } from "../services/workspace/role.service";
// Import other services as needed

/**
 * Hook to fetch and cache filter options in Redux
 * 
 * Automatically fetches data ONCE and stores in Redux.
 * Subsequent calls return cached data - NO repeated API calls!
 * 
 * @param {Array<string>} optionTypes - Array of option types to fetch
 * @param {Object} config - Configuration options
 * @param {boolean} config.autoFetch - Auto fetch on mount (default: true)
 * @param {boolean} config.forceRefresh - Force refresh even if cached (default: false)
 * 
 * @returns {Object} Filter options data
 * 
 * @example
 * // Simple usage - auto fetches and caches
 * const { departments, designations } = useFilterOptions(['departments', 'designations']);
 * 
 * @example
 * // With force refresh
 * const { departments } = useFilterOptions(['departments'], { forceRefresh: true });
 */
export const useFilterOptions = (optionTypes = [], config = {}) => {
  const { autoFetch = true, forceRefresh = false } = config;
  const dispatch = useDispatch();

  // Selectors
  const departments = useSelector(selectDepartments);
  const designations = useSelector(selectDesignations);
  const designationsByDepartment = useSelector(selectDesignationsByDepartment);
  const roles = useSelector(selectRoles);
  const branches = useSelector(selectBranches);
  const categories = useSelector(selectCategories);
  const vendors = useSelector(selectVendors);
  const staff = useSelector(selectStaff);
  const customers = useSelector(selectCustomers);
  const leadSources = useSelector(selectLeadSources);
  const leadStatuses = useSelector(selectLeadStatuses);

  // Fetch functions
  const fetchDepartments = async (force = false) => {
    if (departments.loaded && !forceRefresh && !force) return; // Already loaded
    
    dispatch(setDepartmentsLoading());
    try {
      const response = await getDepartmentList();
      const data = convertToLabelValue(response.department.data || []);
      dispatch(setDepartmentsSuccess(data));
    } catch (error) {
      console.error("Failed to fetch departments:", error);
      dispatch(setDepartmentsError(error.message));
    }
  };

  const fetchDesignations = async (force = false) => {
    if (designations.loaded && !forceRefresh && !force) return;
    
    dispatch(setDesignationsLoading());
    try {
      const response = await getDesignationList();
      const data = convertToLabelValue(response.designation.data || []);
      dispatch(setDesignationsSuccess(data));
    } catch (error) {
      console.error("Failed to fetch designations:", error);
      dispatch(setDesignationsError(error.message));
    }
  };

  const fetchDesignationsByDepartment = async (departmentId, force = false) => {
    if (!departmentId) return;
    
    // Check if already loaded for this department
    if (designationsByDepartment.data[departmentId] && !forceRefresh && !force) {
      return designationsByDepartment.data[departmentId];
    }
    
    dispatch(setDesignationsByDepartmentLoading());
    try {
      const response = await getDesignationList({ department_id: departmentId });
      const data = convertToLabelValue(response.designation.data || []);
      dispatch(setDesignationsByDepartmentSuccess({ departmentId, data }));
      return data;
    } catch (error) {
      console.error("Failed to fetch designations by department:", error);
      dispatch(setDesignationsByDepartmentError(error.message));
      return [];
    }
  };

  const fetchRoles = async (force = false) => {
    if (roles.loaded && !forceRefresh && !force) return;
    
    dispatch(setRolesLoading());
    try {
      const response = await getRoleList();
      const data = convertToLabelValue(response.role.data || []);
      dispatch(setRolesSuccess(data));
    } catch (error) {
      console.error("Failed to fetch roles:", error);
      dispatch(setRolesError(error.message));
    }
  };

  const fetchBranches = async (force = false) => {
    if (branches.loaded && !forceRefresh && !force) return;
    
    dispatch(setBranchesLoading());
    try {
      // const response = await getBranchList();
      // const data = convertToLabelValue(response.branch.data || []);
      // dispatch(setBranchesSuccess(data));
      
      // Placeholder - add your branch service
      dispatch(setBranchesSuccess([]));
    } catch (error) {
      console.error("Failed to fetch branches:", error);
      dispatch(setBranchesError(error.message));
    }
  };

  // Add other fetch functions as needed...

  // Fetch based on requested types
  useEffect(() => {
    if (!autoFetch) return;

    optionTypes.forEach((type) => {
      switch (type) {
        case 'departments':
          fetchDepartments();
          break;
        case 'designations':
          fetchDesignations();
          break;
        case 'roles':
          fetchRoles();
          break;
        case 'branches':
          fetchBranches();
          break;
        // Add other cases as needed
        default:
          console.warn(`Unknown filter option type: ${type}`);
      }
    });
  }, [optionTypes.join(','), forceRefresh]);

  // Manual refresh function
  const refresh = (optionType, departmentId = null) => {
    // First clear the Redux state
    dispatch(refreshOption(optionType));
    
    // Then force fetch with force=true parameter
    switch (optionType) {
      case 'departments':
        fetchDepartments(true); // ✨ Force fetch
        break;
      case 'designations':
        fetchDesignations(true); // ✨ Force fetch
        break;
      case 'designationsByDepartment':
        if (departmentId) {
          fetchDesignationsByDepartment(departmentId, true); // ✨ Force fetch
        }
        break;
      case 'roles':
        fetchRoles(true); // ✨ Force fetch
        break;
      case 'branches':
        fetchBranches(true); // ✨ Force fetch
        break;
      default:
        console.warn(`Unknown filter option type: ${optionType}`);
    }
  };

  return {
    // Data with loading states
    departments: departments.data,
    departmentsLoading: departments.loading,
    departmentsError: departments.error,
    
    designations: designations.data,
    designationsLoading: designations.loading,
    designationsError: designations.error,
    
    designationsByDepartment: designationsByDepartment.data,
    designationsByDepartmentLoading: designationsByDepartment.loading,
    designationsByDepartmentError: designationsByDepartment.error,
    
    roles: roles.data,
    rolesLoading: roles.loading,
    rolesError: roles.error,
    
    branches: branches.data,
    branchesLoading: branches.loading,
    branchesError: branches.error,
    
    categories: categories.data,
    categoriesLoading: categories.loading,
    categoriesError: categories.error,
    
    vendors: vendors.data,
    vendorsLoading: vendors.loading,
    vendorsError: vendors.error,
    
    staff: staff.data,
    staffLoading: staff.loading,
    staffError: staff.error,
    
    customers: customers.data,
    customersLoading: customers.loading,
    customersError: customers.error,
    
    leadSources: leadSources.data,
    leadSourcesLoading: leadSources.loading,
    leadSourcesError: leadSources.error,
    
    leadStatuses: leadStatuses.data,
    leadStatusesLoading: leadStatuses.loading,
    leadStatusesError: leadStatuses.error,
    
    // Utility functions
    refresh,
    fetchDesignationsByDepartment,
  };
};

export default useFilterOptions;

